import { BaseTable, Button, Card, DateInput, HeaderMenu, Input, Label, NumberInput, Select, TableHeader, Textarea } from "components"
import { JournalList, ModalDetailBillService, PaymentBillServiceCode } from "."
import { useApprovalStore } from "../../utils"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useContext, useEffect } from "react"
import { DisabledContext } from "../../contexts"
import { AccountContext } from "contexts"
import { APPROVAL, useBalance, useToggle } from "utils"
import { BillContext } from "../contexts"
import { SelectOptionType } from "types"
import { TbInfoCircle } from "react-icons/tb"
import moment from "moment"
import { PaymentBillServiceSchema } from "../schema"

type InputSectionProps = {
  buttonsection?: JSX.Element
  disable?: boolean
}

export const InputSection = ({ buttonsection, disable }: InputSectionProps) => {
  const { isEdit, isView } = useApprovalStore()
  const disabled = useContext(DisabledContext)
  const accountContext = useContext(AccountContext)
  const billContext = useContext(BillContext)

  const linkBillToggle = useToggle(false)

  const { refetch } = useBalance("/spendmoney/balance")

  const { formState, control, setValue } = useFormContext<PaymentBillServiceSchema>()

  const coa_id = useWatch({ control, name: "coa_id" })
  const transaction_date = useWatch({ control, name: "transaction_date" })
  const deposit_amount = useWatch({ control, name: "deposit_amount" })
  const payment_amount = useWatch({ control, name: "payment_amount" })
  const vendor_id = useWatch({ control, name: "vendor_id" })

  const totalBalance: number = billContext?.databill?.balance - deposit_amount - payment_amount

  const isBalanceBill = formState?.defaultValues?.trx_code && billContext?.approve_status !== APPROVAL.PENDING
  const VENDOR_DROPDOWN_DATA: SelectOptionType[] = [
    {
      label: billContext?.databill?.vendor_name,
      value: billContext?.databill?.vendor_id
    }
  ]

  useEffect(() => {
    if (coa_id && transaction_date) {
      refetch(coa_id, transaction_date).then((value) => setValue("_balance", value))
    }

    // eslint-disable-next-line
  }, [coa_id, transaction_date])

  return (
    <section className="container my-5 flex flex-col gap-2">
      <HeaderMenu 
        title={!isEdit && !isView ? "DATA ENTRY | PAYMENT BILL SERVICE" : "PAYMENT BILL SERVICE"}
        action={isEdit ? "edit" : (isView ? "view" : "create")}
      >
        <PaymentBillServiceCode value={isEdit ? billContext?.trx_code : (isView ? billContext?.trx_code : undefined)} />
      </HeaderMenu>

      <section className="grid lg:grid-cols-2 gap-3">
        <Controller
          name="coa_id"
          control={control}
          render={({ field, fieldState }) => (
            <Select
              label="PAYMENT FROM ACCOUNT"
              placeholder="Select Account"
              isDisabled={disabled || isView || disable}
              isLoading={accountContext.isLoading}
              options={accountContext?.data}
              error={fieldState.error?.message}
              value={accountContext?.data?.find(item => item.value === field.value)}
              onChange={e => field.onChange(e?.value)}
            />
          )}
        />
        
        <Controller
          control={control}
          name="_balance"
          render={({ field, fieldState }) => (
            <NumberInput
              readOnly
              inputClass="text-right"
              label="BALANCE"
              error={fieldState.error?.message}
              ref={field.ref}
              value={field.value}
              thousandSeparator=","
              decimalSeparator="."
            />
          )}
        />
      </section>

      <Card>
        <Card.Body className="grid lg:grid-cols-2 items-stretch gap-3">
          <section className="flex flex-col gap-3">
            <section className="grow flex flex-col gap-3">
              <Select
                isDisabled
                label="PAY TO"
                options={VENDOR_DROPDOWN_DATA}
                value={VENDOR_DROPDOWN_DATA.find(item => item.value === vendor_id)}
              />
              
              <Controller
                name="memo"
                control={control}
                render={({ field, fieldState }) => (
                  <Textarea
                    {...field}
                    label="MEMO"
                    disabled={disabled || isView || disable}
                    error={fieldState.error?.message}
                  />
                )}
              />
              
              <Textarea
                disabled
                label="BRANCH LOCATION"
                value={billContext?.databill?.address}
              />
            </section>

            <div className="justify-self-end">
              <section className="pt-auto">
                <section className="flex items-end">
                  <section className="grow">
                    <Input
                      disabled
                      inputClass="rounded-r-none"
                      label="LINK TO DELIVERY BILL ID"
                      value={billContext?.databill?.trx_code}
                    />
                  </section>
            
                  <Button type="button" color="primary" onClick={() => {
                    linkBillToggle.toggle()
                    // localStorage.setItem("disableCreatePaymentBill", "disable")
                  }}>
                    <TbInfoCircle />
                  </Button>
                </section>
              </section>
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <Controller
              name="referensi"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label="CC | DEBIT NO | CHEQUE NO"
                  disabled={disabled || isView || disable}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="transaction_date"
              control={control}
              render={({ field, fieldState }) => (
                <DateInput
                  label="PAYMENT DATE"
                  disabled={disabled || isView || disable}
                  error={fieldState.error?.message}
                  selected={moment(field.value).toDate()}
                  ref={field.ref}
                  onChange={(date) => field.onChange(moment(date).format("YYYY-MM-DD"))}
                />
              )}
            />

            <div>
              {isBalanceBill && <></>}
              {!isBalanceBill && <NumberInput
                disabled
                inputClass="text-right"
                label="BALANCE BILL"
                value={billContext?.databill?.balance}
              />}
            </div>
            
            <Controller
              name="deposit_amount"
              control={control}
              render={({ field, fieldState }) => (
                <NumberInput
                  decimalSeparator="."
                  inputClass="text-right"
                  label="PAYMENT FROM DEPOSIT"
                  thousandSeparator=","
                  disabled
                  error={fieldState.error?.message}
                  value={field.value}
                  ref={field.ref}
                  onValueChange={value => field.onChange(value.floatValue ?? 0)}
                />
              )}
            />
            
            <Controller
              name="payment_amount"
              control={control}
              render={({ field, fieldState }) => (
                <NumberInput
                  decimalSeparator="."
                  inputClass="text-right"
                  label="PAYMENT AMOUNT"
                  thousandSeparator=","
                  disabled={disabled || isView || disable}
                  error={fieldState.error?.message}
                  value={field.value}
                  ref={field.ref}
                  onValueChange={value => field.onChange(value.floatValue ?? 0)}
                />
              )}
            />
            
            <div>
              {isBalanceBill && <></>}
              {!isBalanceBill && <NumberInput
                disabled
                decimalSeparator="."
                inputClass="text-right"
                label="BALANCE"
                thousandSeparator=","
                value={totalBalance}
              />}
            </div>
          </section>
        </Card.Body>
      </Card>

      <Label text="JOURNAL ENTRY" />

      <BaseTable>
        <TableHeader>
          <tr>
            <th>DATE</th>
            <th>ID#</th>
            <th>ACCOUNT</th>
            <th>MEMO</th>
            <th>DEBET</th>
            <th>CREDIT</th>
          </tr>
        </TableHeader>

        <tbody>
          <JournalList.PaymentAccount />
          <JournalList.PaymentDeposit />
          <JournalList.Total />
        </tbody>
      </BaseTable>

      {linkBillToggle.isActive && (
        <ModalDetailBillService 
          bill_id={billContext?.bill_id} 
          toggle={() => {
            linkBillToggle.toggle()
            // localStorage.setItem("disableCreatePaymentBill", "enable")
          }} 
        />
      )}

      {buttonsection}
    </section>
  )
}